import { DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { IconComponent } from '@shared/components/icon/icon.component';
import { DateControlPeriodParams, InputComponent } from '@shared/components/input/input.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { FormControlConstants } from '@shared/constants/form-control.constants';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { OverlayService } from '@shared/services/overlay.service';

import {
  ForecastTableGridTimelineCustomMilestoneDatepickerType,
  ForecastTableGridTimelineMilestoneInterface,
  ForecastTableGridTimelinePhaseInterface,
  ForecastTableTimelineOverlayData,
  ForecastTableTimelineOverlayResponse,
} from '../../models/forecast-table-grid.model';

dayjs.extend(isSameOrBefore);
@Component({
  imports: [FormsModule, IconComponent, InputComponent, ReactiveFormsModule, DatePipe],
  selector: 'aux-forecast-table-grid-period-time-modal',
  templateUrl: './forecast-table-grid-period-time-modal.component.html',
})
export class ForecastTableGridPeriodTimeModalComponent implements OnInit {
  private overlayService = inject(OverlayService);

  allMilestones: ForecastTableGridTimelineMilestoneInterface[] = [];
  customEndDateControl = new FormControl();
  readonly customMilestoneDatepickerType = ForecastTableGridTimelineCustomMilestoneDatepickerType;
  customStartDateControl = new FormControl();
  readonly formControlConstants = FormControlConstants;
  phases: ForecastTableGridTimelinePhaseInterface[] = [];
  ref =
    inject<
      CustomOverlayRef<
        ForecastTableTimelineOverlayResponse | null,
        ForecastTableTimelineOverlayData
      >
    >(CustomOverlayRef);
  selectedCustomEndMilestone:
    | ForecastTableGridTimelineCustomMilestoneDatepickerType.END_DATE
    | ForecastTableGridTimelineMilestoneInterface
    | null = null;
  selectedCustomStartMilestone:
    | ForecastTableGridTimelineCustomMilestoneDatepickerType.START_DATE
    | ForecastTableGridTimelineMilestoneInterface
    | null = null;
  selectedPhase: ForecastTableGridTimelinePhaseInterface | null = null;

  ngOnInit(): void {
    // Assign timeline phases
    this.phases = this.ref.data?.timelinePhases || [];
    // Assign timeline milestones
    this.allMilestones = this.ref.data?.timelineMilestones || [];
    // Assign selected phase
    this.assignSelectedPhase();
  }

  assignSelectedPhase(): void {
    if (!this.ref.data?.settings) {
      return;
    }

    const {
      end_milestone,
      milestone_category,
      period_end_date,
      period_start_date,
      start_milestone,
    } = this.ref.data.settings;

    if (milestone_category) {
      const phase = this.phases.find(
        (timelinePhase) => timelinePhase.cat_id === milestone_category.id
      );

      if (phase) {
        this.selectedPhase = phase;
      }
    } else {
      this.selectedPhase = this.phases[this.phases.length - 1];

      const startMilestone = this.allMilestones.find(
        (milestone) => milestone.id === start_milestone?.id
      );
      const endMilestone = this.allMilestones.find(
        (milestone) => milestone.id === end_milestone?.id
      );

      if (period_start_date) {
        this.selectedCustomStartMilestone =
          ForecastTableGridTimelineCustomMilestoneDatepickerType.START_DATE;
        this.customStartDateControl.setValue(period_start_date);
      } else if (start_milestone && startMilestone) {
        this.selectedCustomStartMilestone = startMilestone;
      }

      if (period_end_date) {
        this.selectedCustomEndMilestone =
          ForecastTableGridTimelineCustomMilestoneDatepickerType.END_DATE;
        this.customEndDateControl.setValue(period_end_date);
      } else if (end_milestone && endMilestone) {
        this.selectedCustomEndMilestone = endMilestone;
      }
    }
  }

  confirmEndDate(): boolean {
    const endDate = dayjs(this.customEndDateControl.value);

    return endDate.isSameOrBefore(this.trialTimelinePeriod?.endDate);
  }

  confirmStartDate(): boolean {
    const startDate = dayjs(this.customStartDateControl.value);

    return startDate.isSameOrAfter(this.trialTimelinePeriod?.startDate);
  }

  customEndDate(): string {
    return this.selectedCustomEndMilestone ===
      ForecastTableGridTimelineCustomMilestoneDatepickerType.END_DATE
      ? this.customEndDateControl.value
      : (this.selectedCustomEndMilestone?.end_date ?? '');
  }

  customStartDate(): string {
    return this.selectedCustomStartMilestone ===
      ForecastTableGridTimelineCustomMilestoneDatepickerType.START_DATE
      ? this.customStartDateControl.value
      : (this.selectedCustomStartMilestone?.start_date ?? '');
  }

  isCustomDateUnValidAndNotDisabled(): boolean {
    return (
      (!this.isCustomStartDateControlDisabled() && !this.customStartDateControl.valid) ||
      (!this.isCustomEndDateControlDisabled() && !this.customEndDateControl.valid)
    );
  }

  isCustomEndDateControlDisabled(): boolean {
    return (
      this.selectedCustomEndMilestone !==
      ForecastTableGridTimelineCustomMilestoneDatepickerType.END_DATE
    );
  }

  isCustomStartDateControlDisabled(): boolean {
    return (
      this.selectedCustomStartMilestone !==
      ForecastTableGridTimelineCustomMilestoneDatepickerType.START_DATE
    );
  }

  onSave() {
    // If no selected phase
    if (!this.selectedPhase) {
      this.ref.close(null);

      return;
    }

    // If selected phase is Custom
    if (this.selectedPhase.name !== 'Custom') {
      const { cat_id, firstMilestone, lastMilestone } = this.selectedPhase;

      if (firstMilestone && lastMilestone) {
        this.ref.close({
          endDate: null,
          endMilestoneId: lastMilestone.id,
          phaseId: cat_id,
          startDate: null,
          startMilestoneId: firstMilestone.id,
        });

        return;
      }
    }

    // If selected phase is anything but Custom
    // and includes a start/end milestone
    if (
      this.selectedPhase.name === 'Custom' &&
      this.selectedCustomStartMilestone &&
      this.selectedCustomEndMilestone
    ) {
      if (this.isCustomDateUnValidAndNotDisabled()) {
        return;
      }

      if (this.customEndDate() < this.customStartDate()) {
        this.overlayService.error(MessagesConstants.START_DATE_BEFORE_END);

        return;
      }

      const closeData: ForecastTableTimelineOverlayResponse = {
        endDate: null,
        endMilestoneId: '',
        phaseId: null,
        startDate: null,
        startMilestoneId: '',
      };

      if (
        this.selectedCustomStartMilestone ===
        ForecastTableGridTimelineCustomMilestoneDatepickerType.START_DATE
      ) {
        if (!this.confirmStartDate()) {
          return;
        }

        closeData.startDate = this.customStartDateControl.value;
      } else {
        closeData.startMilestoneId = this.selectedCustomStartMilestone.id;
      }

      if (
        this.selectedCustomEndMilestone ===
        ForecastTableGridTimelineCustomMilestoneDatepickerType.END_DATE
      ) {
        if (!this.confirmEndDate()) {
          return;
        }

        closeData.endDate = this.customEndDateControl.value;
      } else {
        closeData.endMilestoneId = this.selectedCustomEndMilestone.id;
      }

      this.ref.close(closeData);
    }
  }

  selectDateInputRadio(selectingStart: boolean): void {
    if (selectingStart) {
      this.selectedCustomStartMilestone =
        ForecastTableGridTimelineCustomMilestoneDatepickerType.START_DATE;
    } else {
      this.selectedCustomEndMilestone =
        ForecastTableGridTimelineCustomMilestoneDatepickerType.END_DATE;
    }
  }

  get trialTimelinePeriod(): DateControlPeriodParams {
    return {
      endDate: this.allMilestones[this.allMilestones.length - 1].end_date,
      startDate: this.allMilestones[0].start_date,
    };
  }
}
